import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import http from '../utils/http';
import { RootState } from './store';

export interface ListItem {
    label: string | number;
    value: string | number;
    isChecked: boolean;
}

export interface DataCriterion {
    year: number | null;
    dataType: 'REVENUES' | 'EXPENDITURES' | 'LONG-TERM_DEBT' | 'POPULATION' | 'BALANCE_SHEET' | null;
    entityType: 'COUNTY' | 'CITY' | 'SPECIAL_DISTRICT' | 'OTHER_ENTITY' | null | "NULL";
    entityId: number | null;
    group: string | null;
}

export interface DataPoint {
    x: string;
    y: number;
    z?: number | null;
    label: string;
}

export interface DataPointNode {
    x: string;
    y: number;
    children?: Array<DataPointNode>;
}


export interface VisualizationState {
    geoJSON: any;
    barChartDataPoints: Array<DataPoint>;
    pieChartDataPointNodes: DataPointNode | null;
    status: 'idle' | 'loading' | 'failed';
}

const initialState: VisualizationState = {
    geoJSON: null,
    barChartDataPoints: [],
    pieChartDataPointNodes: null,
    status: 'idle'
}

export const getDataTierByYear = createAsyncThunk(
    'Visualization/GetDataTierByYear',
    async (criterion: DataCriterion): Promise<DataPoint | null> => {
        try {
            const result = await http<DataCriterion, DataPoint>({
                path: `/visualization/DataPointByYear/${criterion.dataType}/${criterion.year}`,
                method: 'get'
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
)

export const getDataTierByYearAndEntityType = createAsyncThunk(
    'Visualization/GetDataTierByYearAndEntityType',
    async (criterion: DataCriterion): Promise<Array<DataPoint> | null> => {
        try {
            const result = await http<DataCriterion, Array<DataPoint>>({
                path: `/visualization/DataPointByYearAndEntityType/${criterion.dataType}/${criterion.year}/${criterion.entityType}`,
                method: 'get'
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
)

export const getYearlyDataForEntities = createAsyncThunk(
    'Visualization/GetYearlyDataForEntities',
    async (criterion: DataCriterion): Promise<Array<DataPoint> | null> => {
        try {
            const result = await http<DataCriterion, Array<DataPoint>>({
                path: `/visualization/GetYearlyDataForEntities/${criterion.dataType}/${criterion.year}`,
                method: 'get'
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
)

export const getDataTierByYearAndEntity = createAsyncThunk(
    'Visualization/GetDataTierByYearAndEntity',
    async (criterion: DataCriterion): Promise<DataPoint | null> => {
        try {
            const result = await http<DataCriterion, DataPoint>({
                path: `/visualization/DataPointByYearAndEntity/${criterion.dataType}/${criterion.year}/${criterion.entityId}`,
                method: 'get'
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
)

export const getDataTierByYearAndEntityFundGroup = createAsyncThunk(
    'Visualization/GetDataTierByYearAndEntityFundGroup',
    async (criterion: DataCriterion): Promise<Array<DataPoint> | null> => {
        try {
            const result = await http<DataCriterion, Array<DataPoint>>({
                path: `/visualization/DataPointByYearAndEntityFundGroup/${criterion.dataType}/${criterion.year}/${criterion.entityId}`,
                method: 'get'
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
)

export const getDataTierByYearEntityAccount = createAsyncThunk(
    'Visualization/GetDataTierByYearEntityAccount',
    async (criterion: DataCriterion): Promise<Array<DataPoint> | null> => {
        try {
            const result = await http<DataCriterion, Array<DataPoint>>({
                path: `/visualization/DataPointByYearEntityAccount/${criterion.dataType}/${criterion.year}/${criterion.entityId}`,
                method: 'get'
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            } else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    })

export const getDataPointNodeByYearEntityAccount = createAsyncThunk(
    'Visualization/GetDataPointNodeByYearEntityAccount',
    async (criterion: DataCriterion): Promise<DataPointNode | null> => {

        try {
            const result = await http<DataCriterion, DataPointNode>({
                path: `/visualization/DataPointNodeByYearEntityAccount/${criterion.dataType}/${criterion.year}/${criterion.entityId}`,
                method: 'get'
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            } else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getDataTierByYearAndEntitySubFundGroup = createAsyncThunk(
    'Visualization/GetDataTierByYearAndEntitySubFundGroup',
    async (criterion: DataCriterion): Promise<Array<DataPoint> | null> => {
        try {
            const result = await http<DataCriterion, Array<DataPoint>>({
                path: `/visualization/DataPointByYearAndEntitySubFundGroup/${criterion.dataType}/${criterion.year}/${criterion.entityId}/${criterion.group}`,
                method: 'get'
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
)

export const getDataTierByYearAndEntityIndividualSubFundGroup = createAsyncThunk(
    'Visualization/GetDataTierByYearAndEntityIndividualSubFundGroup',
    async (criterion: DataCriterion): Promise<Array<DataPoint> | null> => {
        try {
            const result = await http<DataCriterion, Array<DataPoint>>({
                path: `/visualization/DataPointByYearAndEntityIndividualSubFundGroup/${criterion.dataType}/${criterion.year}/${criterion.entityId}/${criterion.group}`,
                method: 'get'
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
)

export const getStateDataTierByYearAndEntityType = createAsyncThunk(
    'visualization/GetStateDataTierByYearAndEntityType',
    async (criterion: DataCriterion): Promise<Array<DataPoint> | null> => {
        try {
            const result = await http<DataCriterion, Array<DataPoint>>({
                path: `/visualization/StateDataPointByYearAndEntityType/${criterion.dataType}/${criterion.year}/${criterion.entityType}`,
                method: 'get'
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
)

export const getGeoJSONData = createAsyncThunk(
    'Visualization/GetGeoJSON',
    async (): Promise<{} | null> => {
        try {
            const result = await http<null, {}>({
                path: `/visualization/GeoJSON`,
                method: 'get'
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
)


export const visualizationSlice = createSlice({
    name: 'Visualization',
    initialState,
    reducers: {

        resetState(state: any) {
            state = initialState;
        },
        clearBarChartData(state: any) {
            state.barChartDataPoints = [];
        },
        clearPieChartData(state: any) {
            state.pieChartDataPointNodes = null;
        }
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(getDataTierByYear.pending, (state: any) => {
                state.status = 'loading';
            })
            .addCase(getDataTierByYear.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    if (!state.barChartDataPoints) {
                        state.barChartDataPoints = [];
                    }
                    state.barChartDataPoints.push(action.payload);
                }
                state.status = 'idle';
            })

            .addCase(getDataTierByYearAndEntityType.pending, (state: any) => {
                state.status = 'loading';
            })
            .addCase(getDataTierByYearAndEntityType.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    state.barChartDataPoints = action.payload;
                }
                state.status = 'idle';
            })

            .addCase(getDataTierByYearAndEntity.pending, (state: any) => {
                state.status = 'loading';
            })
            .addCase(getDataTierByYearAndEntity.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    let result: Array<DataPoint> = [];
                    result.push(action.payload);
                    state.barChartDataPoints = result;
                }
                state.status = 'idle';
            })

            .addCase(getDataTierByYearAndEntityFundGroup.pending, (state: any) => {
                state.status = 'loading';
            })
            .addCase(getDataTierByYearAndEntityFundGroup.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    state.barChartDataPoints = action.payload;
                }
                state.status = 'idle';
            })

            .addCase(getDataTierByYearAndEntitySubFundGroup.pending, (state: any) => {
                state.status = 'loading';
            })
            .addCase(getDataTierByYearAndEntitySubFundGroup.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    state.barChartDataPoints = action.payload;
                }
                state.status = 'idle';
            })

            .addCase(getDataTierByYearAndEntityIndividualSubFundGroup.pending, (state: any) => {
                state.status = 'loading';
            })
            .addCase(getDataTierByYearAndEntityIndividualSubFundGroup.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    state.barChartDataPoints = action.payload;
                }
                state.status = 'idle';
            })

            .addCase(getStateDataTierByYearAndEntityType.pending, (state: any) => {
                state.status = 'loading';
            })
            .addCase(getStateDataTierByYearAndEntityType.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    state.barChartDataPoints = action.payload;
                }
                state.status = 'idle';
            })

            .addCase(getGeoJSONData.pending, (state: any) => {
                state.status = 'loading';
            })
            .addCase(getGeoJSONData.fulfilled, (state: any, action: any) => {
                if (action.payload) {

                    state.geoJSON = JSON.parse(action.payload);

                }
                state.status = 'idle';
            })

            .addCase(getYearlyDataForEntities.pending, (state: any) => {
                state.status = 'loading';
                state.barChartDataPoints = [];
            })
            .addCase(getYearlyDataForEntities.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    state.barChartDataPoints = action.payload;
                }
                state.status = 'idle';
            })

            .addCase(getDataTierByYearEntityAccount.pending, (state: any) => {
                state.status = 'loading';
            })
            .addCase(getDataTierByYearEntityAccount.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    state.barChartDataPoints = action.payload;
                }
                state.status = 'idle';
            })

            .addCase(getDataPointNodeByYearEntityAccount.pending, (state: any) => {
                state.status = 'loading';
            })
            .addCase(getDataPointNodeByYearEntityAccount.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    state.pieChartDataPointNodes = action.payload;
                }
                state.status = 'idle';
            })
    }
})

export const { resetState, clearBarChartData, clearPieChartData } = visualizationSlice.actions;

export const selectLoadingState = (state: RootState) => state.visualization.status;

export const selectGeoJSONData = (state: RootState) => state.visualization.geoJSON;

export const selectBarChartDataPoints = (state: RootState) => state.visualization.barChartDataPoints;

export const selectPieChartataPointNodes = (state: RootState) => state.visualization.pieChartDataPointNodes;

export default visualizationSlice.reducer;